import { useEffect, useRef } from 'react'

// A one-line "are you sure" that opens in place (under the card or row it's
// about) rather than a modal over the page. Used by narration, voice memos and
// History.

// The small versions of ui.tsx's buttons (the app's primary, "+ New", nav idle).
export const smallBtn = {
  primary:
    'inline-flex items-center justify-center whitespace-nowrap rounded-xl bg-accent px-3 py-1 text-[13px] font-medium text-accent-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50',
  outline:
    'inline-flex items-center justify-center whitespace-nowrap rounded-xl border border-border/40 px-2.5 py-1 text-[13px] font-medium text-muted-foreground transition-all hover:border-accent/40 hover:bg-muted/20 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50',
  ghost:
    'inline-flex items-center justify-center whitespace-nowrap rounded-xl px-2 py-1 text-[13px] font-medium text-muted-foreground transition-all hover:bg-muted/30 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50',
}

export function InlineConfirm({
  message,
  detail,
  confirmLabel,
  cancelLabel = 'Cancel',
  busy = false,
  focus = 'confirm',
  onConfirm,
  onCancel,
  onDismiss,
  className = '',
}: {
  message: string
  detail?: string
  confirmLabel: string
  cancelLabel?: string
  busy?: boolean
  /** Which button takes focus: the safe one ('cancel') for destructive asks. */
  focus?: 'confirm' | 'cancel'
  onConfirm(): void
  onCancel(): void
  /** Escape; defaults to onCancel. */
  onDismiss?(): void
  className?: string
}) {
  const confirmRef = useRef<HTMLButtonElement>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    ;(focus === 'cancel' ? cancelRef : confirmRef).current?.focus({ preventScroll: true })
  }, [focus])
  return (
    <div
      role="alertdialog"
      aria-label={message}
      onKeyDown={(e) => {
        e.stopPropagation()
        if (e.key === 'Escape') (onDismiss ?? onCancel)()
      }}
      className={`flex flex-wrap items-center gap-x-2 gap-y-2 rounded-lg border border-border/40 bg-card px-3 py-2.5 font-sans text-[13px] text-foreground/80 ${className}`}
    >
      <span className="min-w-[10rem] flex-1">
        {message}
        {detail && <span className="block text-[11.5px] text-muted-foreground">{detail}</span>}
      </span>
      <span className="flex gap-1.5">
        <button ref={confirmRef} type="button" disabled={busy} onClick={onConfirm} className={smallBtn.primary}>
          {confirmLabel}
        </button>
        <button ref={cancelRef} type="button" disabled={busy} onClick={onCancel} className={smallBtn.outline}>
          {cancelLabel}
        </button>
      </span>
    </div>
  )
}
